import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiPut, apiDelete, apiUpload, formatMoney, setCurrencyMeta, t, showAiConsentModal } from "../api_helper"
import { icon } from "../lib/icons"
import { offlineStore } from "../lib/offline_store"
import { networkStatus } from "../lib/network_status"
import { isOfflineError, alertOffline } from "../lib/offline_ui"

export default class extends Controller {
  static targets = ["expenses", "income", "balance", "transactions", "accountCards", "customRange", "startDate", "endDate", "streakBadge", "budgetScoreBadge", "budgetScoreModal", "budgetScoreModalContent", "goalsSummary", "debtsSummary", "forecastWidget", "filters", "accountFilterChips", "categoryFilterChips", "clearFilterBtn", "voiceBtn", "voiceBtnText", "photoBtn", "photoInput", "voiceOverlay", "voiceStatus", "voiceTimer", "voiceStopBtn", "processingOverlay", "processingText", "resultsOverlay", "resultsContent"]

  connect() {
    this.currentPeriod = "month"
    this.currentPage = 1
    this.allTransactions = []
    this.totalPages = 1
    this.customStartDate = null
    this.customEndDate = null
    this.expandedTxId = null
    this.editingTxId = null
    this.categoriesCache = null
    this.filterAccountId = null
    this.filterCategoryId = null
    this.cachedAccounts = null
    this.cachedCategories = null
    this.load()

    if (this.hasTransactionsTarget) {
      this.transactionsTarget.addEventListener("click", (e) => {
        const txRow = e.target.closest("[data-tx-toggle]")
        if (txRow) {
          e.preventDefault()
          const txId = parseInt(txRow.dataset.txToggle)
          this.expandedTxId = this.expandedTxId === txId ? null : txId
          this.renderTransactions()
          return
        }
        const delBtn = e.target.closest("[data-tx-delete]")
        if (delBtn) {
          e.preventDefault()
          e.stopPropagation()
          this.deleteTransaction(delBtn.dataset.txDelete)
          return
        }
        const editBtn = e.target.closest("[data-tx-edit]")
        if (editBtn) {
          e.preventDefault()
          e.stopPropagation()
          this.startEdit(parseInt(editBtn.dataset.txEdit))
          return
        }
        const saveBtn = e.target.closest("[data-tx-save]")
        if (saveBtn) {
          e.preventDefault()
          e.stopPropagation()
          this.saveEdit(parseInt(saveBtn.dataset.txSave))
          return
        }
        const cancelBtn = e.target.closest("[data-tx-cancel]")
        if (cancelBtn) {
          e.preventDefault()
          e.stopPropagation()
          this.cancelEdit()
          return
        }
        const loadMoreBtn = e.target.closest("[data-load-more]")
        if (loadMoreBtn) {
          this.loadMore()
        }
      })
    }
  }

  todayStr() {
    return new Date().toISOString().slice(0, 10)
  }

  buildApiUrl(page = 1) {
    let url = `/api/dashboard?period=${this.currentPeriod}&date=${this.todayStr()}&page=${page}&per_page=20`
    if (this.currentPeriod === "custom" && this.customStartDate && this.customEndDate) {
      url += `&start_date=${this.customStartDate}&end_date=${this.customEndDate}`
    }
    if (this.filterAccountId) url += `&account_id=${this.filterAccountId}`
    if (this.filterCategoryId) url += `&category_id=${this.filterCategoryId}`
    return url
  }

  async load() {
    try {
      this.currentPage = 1
      this.allTransactions = []

      // Load currency metadata (symbols, decimals) from server — best effort, non-blocking
      if (!window._currenciesLoaded) {
        apiGet("/api/currencies").then(res => {
          if (res.currencies) {
            setCurrencyMeta(res.currencies)
            window._currenciesLoaded = true
          }
        }).catch(() => {})
      }

      const data = await apiGet(this.buildApiUrl(1))

      if (data.onboarding_completed === false) {
        window.location.href = "/pwa/onboarding"
        return
      }

      this.allTransactions = data.recent_transactions
      this.totalPages = data.transactions_meta?.total_pages || 1
      this.sharedMode = data.shared_mode || false
      this.displayCurrency = data.currency || "RUB"
      this.isPremium = data.premium || false
      this.render(data)

      // Cache data for offline use
      this.cacheDashboardData(data)
    } catch (e) {
      console.error("Dashboard load error:", e)
      await this.loadFromCache(e)
    }
  }

  async cacheDashboardData(data) {
    try {
      if (data.recent_transactions?.length) {
        await offlineStore.cacheData("cached_transactions", data.recent_transactions)
      }
      if (data.accounts?.length) {
        await offlineStore.cacheData("cached_accounts", data.accounts)
      }
      console.debug("[offline] cached", data.recent_transactions?.length || 0, "tx,", data.accounts?.length || 0, "accounts")
    } catch (e) {
      console.error("[offline] cache write error:", e)
    }
  }

  async loadFromCache(originalError) {
    try {
      const transactions = await offlineStore.getCachedData("cached_transactions")
      const accounts = await offlineStore.getCachedData("cached_accounts")
      console.debug("[offline] cache read:", transactions.length, "tx,", accounts.length, "accounts")

      if (!transactions.length && !accounts.length) {
        if (isOfflineError(originalError)) {
          // No cache yet — show offline placeholder with auto-retry
          const { showOfflinePlaceholder } = await import("../lib/offline_ui")
          showOfflinePlaceholder(this.hasTransactionsTarget ? this.transactionsTarget : null, () => this.load())
        } else if (originalError) {
          this.renderLoadError(originalError.message)
        }
        return
      }

      this.allTransactions = transactions
      this.totalPages = 1
      this.displayCurrency = accounts[0]?.currency || "RUB"

      const data = {
        recent_transactions: transactions,
        accounts: accounts,
        total_expenses: transactions.filter(tx => tx.kind === "expense").reduce((s, tx) => s + (tx.display_amount ?? tx.amount), 0),
        total_income: transactions.filter(tx => tx.kind === "income").reduce((s, tx) => s + (tx.display_amount ?? tx.amount), 0),
        total_balance: accounts.reduce((s, a) => s + (a.balance || 0), 0),
        currency: this.displayCurrency
      }

      this.render(data)
      this.renderOfflineBanner()
    } catch (e) {
      console.error("Cache load error:", e)
      if (isOfflineError(originalError)) {
        const { showOfflinePlaceholder } = await import("../lib/offline_ui")
        showOfflinePlaceholder(this.hasTransactionsTarget ? this.transactionsTarget : null, () => this.load())
      } else if (originalError) {
        this.renderLoadError(originalError.message)
      }
    }
  }

  renderOfflineBanner() {
    if (!this.hasTransactionsTarget) return
    const banner = document.createElement("div")
    banner.className = "text-center text-xs text-slate-400 py-2 mb-2 bg-slate-50 dark:bg-slate-800/50 rounded-xl"
    banner.textContent = t("errors.offline_mode") || "Offline mode — showing cached data"
    this.transactionsTarget.parentNode.insertBefore(banner, this.transactionsTarget)

    // Remove banner and reload when back online
    const unsub = networkStatus.onChange((online) => {
      if (online) {
        unsub()
        banner.remove()
        this.load()
      }
    })
  }

  renderLoadError(reason) {
    if (!this.hasTransactionsTarget) return
    const message = t("errors.load_failed") || "Failed to load data. Check your connection."
    this.transactionsTarget.innerHTML = `
      <div class="flex flex-col items-center justify-center py-16 px-6">
        <span class="mb-3">${icon("trending-up", "w-10 h-10 text-slate-300")}</span>
        <div class="text-sm text-slate-400 dark:text-slate-500 mb-4 text-center">${message}</div>
        <button data-action="click->pwa-dashboard#load"
                class="px-6 py-2.5 rounded-xl text-sm font-semibold bg-indigo-500 text-white cursor-pointer hover:bg-indigo-600 transition-colors">
          ${t("errors.retry") || "Retry"}
        </button>
      </div>`
  }

  async loadMore() {
    if (this.currentPage >= this.totalPages) return
    this.currentPage++
    try {
      const data = await apiGet(this.buildApiUrl(this.currentPage))
      this.allTransactions = [...this.allTransactions, ...data.recent_transactions]
      this.totalPages = data.transactions_meta?.total_pages || 1
      this.renderTransactions()
    } catch (e) {
      console.error("Load more error:", e)
    }
  }

  render(data) {
    const currency = data.currency || "RUB"
    if (this.hasExpensesTarget) {
      this.expensesTarget.textContent = formatMoney(data.total_expenses, currency)
      this.expensesTarget.classList.add("amount-animate")
    }
    if (this.hasIncomeTarget) {
      this.incomeTarget.textContent = formatMoney(data.total_income, currency)
      this.incomeTarget.classList.add("amount-animate")
    }
    if (this.hasBalanceTarget) {
      this.balanceTarget.textContent = formatMoney(data.total_balance, currency)
      this.balanceTarget.classList.add("amount-animate")
    }

    if (this.hasAccountCardsTarget && data.accounts) {
      if (!this.cachedAccounts) this.cachedAccounts = data.accounts

      let cards = data.accounts.map(a => {
        const isActive = this.filterAccountId == a.id
        const borderClass = isActive
          ? "border-2 p-[7px_11px]"
          : "border-2 border-transparent p-[7px_11px]"
        const bgOpacity = isActive ? "18%" : "10%"
        return `
        <div data-action="click->pwa-dashboard#filterByAccount" data-account-id="${a.id}"
             class="flex-shrink-0 rounded-xl cursor-pointer hover:opacity-80 transition-opacity ${borderClass}"
             style="background-color: color-mix(in srgb, ${a.color} ${bgOpacity}, transparent);${isActive ? ` border-color: ${a.color};` : ` border-left: 3px solid ${a.color};`} min-width: 110px;">
          <div class="text-base mb-0.5">${a.icon}</div>
          <div class="text-xs font-medium truncate text-slate-400">${a.name}</div>
          <div class="text-xs font-bold text-slate-900 dark:text-slate-100">${formatMoney(a.balance, a.currency)}</div>
        </div>`
      }).join("")

      cards += `
        <a href="/pwa/accounts"
           class="flex-shrink-0 rounded-xl flex items-center justify-center bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
           style="min-width: 44px; min-height: 60px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgb(148, 163, 184)" stroke-width="2" stroke-linecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </a>`

      this.accountCardsTarget.innerHTML = cards
    }

    this.renderCategoryFilter(data)
    if (data.streak) this.renderStreak(data.streak)
    if (data.goals_summary) this.renderGoalsSummary(data.goals_summary, currency)
    if (data.debts_summary) this.renderDebtsSummary(data.debts_summary, currency)
    if (data.budget_score) this.renderBudgetScore(data.budget_score)
    this.loadForecastWidget(currency)
    this.renderTransactions()
  }

  renderCategoryFilter(data) {
    if (!this.hasFiltersTarget) return

    if (!this.filterAccountId && !this.filterCategoryId) {
      const seen = new Map()
      ;(data.recent_transactions || []).forEach(tx => {
        if (!seen.has(tx.category.id)) seen.set(tx.category.id, tx.category)
      })
      this.cachedCategories = Array.from(seen.values())
    }

    const categories = this.cachedCategories || []
    const accounts = this.cachedAccounts || data.accounts || []
    if (categories.length < 1 && accounts.length < 2) {
      this.filtersTarget.classList.add("hidden")
      return
    }

    this.filtersTarget.classList.remove("hidden")

    if (this.hasClearFilterBtnTarget) {
      if (this.filterAccountId || this.filterCategoryId) {
        this.clearFilterBtnTarget.classList.remove("hidden")
      } else {
        this.clearFilterBtnTarget.classList.add("hidden")
      }
    }

    // Account filter chips
    if (this.hasAccountFilterChipsTarget && accounts.length > 1) {
      this.accountFilterChipsTarget.innerHTML = accounts.map(a => {
        const isActive = this.filterAccountId == a.id
        const bgStyle = isActive ? `background-color: ${a.color}; color: white;` : "background-color: rgb(248, 250, 252); color: rgb(148, 163, 184);"
        const darkBgClass = isActive ? "" : "dark:bg-slate-800/50 dark:text-slate-400"
        return `<button data-action="click->pwa-dashboard#filterByAccount" data-account-id="${a.id}"
                        class="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-all whitespace-nowrap ${darkBgClass}"
                        style="${bgStyle}">
                  ${a.icon} ${a.name}
                </button>`
      }).join("")
    }

    // Category filter chips
    if (this.hasCategoryFilterChipsTarget) {
      this.categoryFilterChipsTarget.innerHTML = categories.map(c => {
        const isActive = this.filterCategoryId == c.id
        const bgStyle = isActive
          ? `background-color: ${c.color}; color: white;`
          : `background-color: color-mix(in srgb, ${c.color} 10%, transparent); color: ${c.color}; border: 1.5px solid color-mix(in srgb, ${c.color} 30%, transparent);`
        return `<button data-action="click->pwa-dashboard#filterByCategory" data-category-id="${c.id}"
                        class="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-all whitespace-nowrap"
                        style="${bgStyle}">
                  <span class="flex-shrink-0">${c.icon}</span> <span class="truncate max-w-[80px]">${c.name}</span>
                </button>`
      }).join("")
    }
  }

  filterByAccount(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.accountId
    this.filterAccountId = this.filterAccountId == id ? null : id
    this.load()
  }

  filterByCategory(event) {
    const id = event.currentTarget.dataset.categoryId
    this.filterCategoryId = this.filterCategoryId == id ? null : id
    this.load()
  }

  clearFilters() {
    this.filterAccountId = null
    this.filterCategoryId = null
    this.load()
  }

  renderStreak(streak) {
    if (!this.hasStreakBadgeTarget || streak.current < 1) return
    this.streakBadgeTarget.classList.remove("hidden")
    const fireEmoji = streak.today ? icon("trending-up", "w-4 h-4 inline text-orange-500") : icon("clock", "w-4 h-4 inline text-slate-400")
    const statusText = streak.today
      ? (t("streaks.today_done") || "Recorded today!")
      : (t("streaks.today_missing") || "Add an expense today")
    const statusColor = streak.today ? "text-emerald-500" : "text-slate-400"

    this.streakBadgeTarget.innerHTML = `
      <div class="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white/15">
        <span>${fireEmoji}</span>
        <span class="font-bold">${streak.current}</span>
        <span class="opacity-70">${t("streaks.days") || "days"}</span>
      </div>`
  }

  async loadForecastWidget(currency) {
    if (!this.hasForecastWidgetTarget) return
    try {
      const data = await apiGet("/api/forecast")
      this.forecastWidgetTarget.classList.remove("hidden")
      const label = t("dashboard.forecast_label") || "Forecast"
      const sub = t("dashboard.forecast_next_month") || "next month"
      this.forecastWidgetTarget.innerHTML = `
        <a href="/pwa/forecast" class="block bg-white dark:bg-slate-800 rounded-xl p-3 shadow-sm active:scale-[0.98] transition-transform">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <span class="text-indigo-500"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg></span>
              <span class="text-xs font-semibold text-slate-700 dark:text-slate-200">${label}</span>
            </div>
            <span class="text-sm font-bold text-red-500">
              ${formatMoney(data.total_forecast, data.currency)}
            </span>
          </div>
          <div class="text-xs mt-1 text-slate-400">
            ${sub} · ${data.period_key}
          </div>
        </a>`
    } catch {
      this.forecastWidgetTarget.classList.add("hidden")
    }
  }

  renderGoalsSummary(goals, currency) {
    if (!this.hasGoalsSummaryTarget) return
    if (!goals.count) {
      this.goalsSummaryTarget.classList.add("hidden")
      return
    }
    this.goalsSummaryTarget.classList.remove("hidden")
    const pct = goals.target > 0 ? Math.min((goals.saved / goals.target * 100), 100).toFixed(0) : 0
    this.goalsSummaryTarget.innerHTML = `
      <a href="/pwa/goals" class="block p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
        <div class="flex items-center justify-between mb-1.5">
          <div class="flex items-center gap-1.5">
            <span class="text-sm">${icon("target", "w-4 h-4 inline")}</span>
            <span class="text-xs font-semibold text-slate-900 dark:text-slate-100">${t("dashboard.goals_saved") || "Goals"}</span>
          </div>
          <span class="text-xs font-bold text-slate-900 dark:text-slate-100">${pct}%</span>
        </div>
        <div class="h-1 rounded-sm bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div class="h-full rounded-sm transition-[width] duration-300" style="width: ${pct}%; background-color: #F59E0B;"></div>
        </div>
        <div class="text-xs mt-1 text-slate-400">
          ${formatMoney(goals.saved, currency)} ${t("dashboard.goals_of") || "of"} ${formatMoney(goals.target, currency)}
        </div>
      </a>`
  }

  renderDebtsSummary(debts, currency) {
    if (!this.hasDebtsSummaryTarget) return
    if (!debts.i_owe && !debts.owed_to_me) {
      this.debtsSummaryTarget.classList.add("hidden")
      return
    }
    this.debtsSummaryTarget.classList.remove("hidden")
    this.debtsSummaryTarget.innerHTML = `
      <a href="/pwa/debts" class="block p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-1.5">
            <span class="text-sm">${icon("file-text", "w-4 h-4 inline")}</span>
            <span class="text-xs font-semibold text-slate-900 dark:text-slate-100">${t("debts.title") || "Debts"}</span>
          </div>
        </div>
        <div class="flex justify-between text-xs mt-1">
          <span class="text-red-500">${t("dashboard.debts_owe") || "I owe"}: ${formatMoney(debts.i_owe, currency)}</span>
          <span class="text-emerald-500">${t("dashboard.debts_owed") || "Owed to me"}: ${formatMoney(debts.owed_to_me, currency)}</span>
        </div>
      </a>`
  }

  renderBudgetScore(score) {
    if (!this.hasBudgetScoreBadgeTarget || !score) return
    this.budgetScoreBadgeTarget.classList.remove("hidden")
    this._budgetScoreData = score

    const gradeColors = { A: "#4ADE80", B: "#60A5FA", C: "#FBBF24", D: "#FB923C", F: "#F87171" }
    const gradeColor = gradeColors[score.grade] || "#94A3B8"

    this.budgetScoreBadgeTarget.innerHTML = `
      <button data-action="click->pwa-dashboard#openBudgetScoreModal"
              class="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-white/15 active:bg-white/25 transition-colors" style="cursor: pointer;">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${gradeColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
        <span class="font-bold" style="color: ${gradeColor};">${score.grade}</span>
        <span class="opacity-80">${score.score}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="opacity-60"><polyline points="9 18 15 12 9 6"/></svg>
      </button>`
  }

  openBudgetScoreModal() {
    if (!this.hasBudgetScoreModalTarget || !this._budgetScoreData) return
    const score = this._budgetScoreData
    const gradeColors = { A: "#4ADE80", B: "#60A5FA", C: "#FBBF24", D: "#FB923C", F: "#F87171" }
    const gradeColor = gradeColors[score.grade] || "#94A3B8"
    const breakdown = score.breakdown || {}
    const items = [
      { key: "discipline", label: t("budget_score.discipline") || "Discipline", hint: t("budget_score.discipline_hint") || "How well you stay within budgets" },
      { key: "overspend_days", label: t("budget_score.overspend_days") || "Overspend days", hint: t("budget_score.overspend_days_hint") || "Days when budget was exceeded" },
      { key: "stability", label: t("budget_score.stability") || "Stability", hint: t("budget_score.stability_hint") || "Consistency of daily spending" },
      { key: "balance", label: t("budget_score.balance") || "Balance", hint: t("budget_score.balance_hint") || "Ratio of income to expenses" },
      { key: "regularity", label: t("budget_score.regularity") || "Regularity", hint: t("budget_score.regularity_hint") || "How often you track expenses" }
    ]

    const bars = items.map(({ key, label, hint }) => {
      const val = breakdown[key] || 0
      const barClass = val >= 75 ? "bg-emerald-500" : val >= 50 ? "bg-amber-500" : "bg-red-500"
      return `
        <div class="mb-4">
          <div class="flex items-center justify-between mb-0.5">
            <span class="text-sm font-semibold text-slate-900 dark:text-slate-100">${label}</span>
            <span class="text-sm font-bold text-slate-700 dark:text-slate-200">${val}</span>
          </div>
          <div class="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden mb-1">
            <div class="h-full rounded-full ${barClass} transition-all duration-500" style="width: ${val}%;"></div>
          </div>
          <p class="text-xs text-slate-400 dark:text-slate-500">${hint}</p>
        </div>`
    }).join("")

    const commentHtml = score.comment
      ? `<div class="mt-4 p-3 rounded-xl bg-slate-50 dark:bg-slate-800">
           <p class="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">${this.escapeHtml(score.comment)}</p>
         </div>`
      : ""

    this.budgetScoreModalContentTarget.innerHTML = `
      <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-700 mx-auto mb-5"></div>
      <div class="flex items-center justify-between mb-5">
        <div class="flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${gradeColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
          <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">${t("budget_score.title") || "Budget Health"}</h3>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-3xl font-extrabold leading-none" style="color: ${gradeColor};">${score.grade}</span>
          <span class="text-sm font-medium text-slate-400">${score.score}/100</span>
        </div>
      </div>
      ${bars}
      ${commentHtml}`

    this.budgetScoreModalTarget.style.display = "flex"
  }

  closeBudgetScoreModal() {
    if (this.hasBudgetScoreModalTarget) {
      this.budgetScoreModalTarget.style.display = "none"
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  renderTransactions() {
    if (!this.hasTransactionsTarget) return

    if (!this.allTransactions.length) {
      this.transactionsTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center py-12">
          <span class="mb-2">${icon("file-text", "w-10 h-10 text-slate-300")}</span>
          <div class="text-sm text-slate-400">${t("dashboard.no_transactions")}</div>
        </div>`
      return
    }

    const grouped = this.groupByDate(this.allTransactions)
    let html = ""

    for (const [date, txns] of Object.entries(grouped)) {
      const dayTotal = txns.reduce((sum, tx) => {
        if (tx.transfer_id || tx.correction) return sum
        const amt = tx.display_amount ?? tx.amount
        return sum + (tx.kind === "expense" ? -amt : amt)
      }, 0)
      const dayColor = dayTotal >= 0 ? "text-emerald-500" : "text-red-500"

      html += `
        <div class="flex justify-between items-center mt-4 mb-2 px-1">
          <span class="text-xs font-semibold text-slate-400">${this.formatDateHeader(date)}</span>
          <span class="text-xs font-medium ${dayColor}">${formatMoney(Math.abs(dayTotal), this.displayCurrency || "RUB")}</span>
        </div>`

      txns.forEach(tx => { html += this.transactionRow(tx) })
    }

    if (this.currentPage < this.totalPages) {
      html += `
        <button data-load-more
                class="w-full py-3 mt-3 rounded-xl text-sm font-medium bg-slate-50 dark:bg-slate-800/50 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
          ${t("dashboard.load_more") || "Load more..."}
        </button>`
    }

    this.transactionsTarget.innerHTML = `<div class="animate-fade-in">${html}</div>`
  }

  groupByDate(transactions) {
    const groups = {}
    transactions.forEach(tx => {
      const date = tx.date
      if (!groups[date]) groups[date] = []
      groups[date].push(tx)
    })
    return groups
  }

  formatDateHeader(dateStr) {
    const date = new Date(dateStr + "T00:00:00")
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.getTime() === today.getTime()) return t("dashboard.today") || "Today"
    if (date.getTime() === yesterday.getTime()) return t("dashboard.yesterday") || "Yesterday"

    return date.toLocaleDateString(window.APP_LOCALE === "ru" ? "ru-RU" : "en-US", {
      day: "numeric", month: "short"
    })
  }

  transactionRow(tx) {
    const sign = tx.kind === "expense" ? "-" : "+"
    const colorClass = tx.kind === "expense" ? "text-red-500" : "text-emerald-500"
    const isExpanded = this.expandedTxId === tx.id
    const catBg = tx.category.color ? `color-mix(in srgb, ${tx.category.color} 10%, transparent)` : "rgb(248, 250, 252)"
    const isOwn = !tx.user || tx.user.is_current
    const isDeletable = isOwn && !tx.transfer_id && !tx.correction

    const userName = (this.sharedMode && tx.user) ? tx.user.name : null

    return `
      <div class="overflow-hidden rounded-xl">
        <div class="flex items-center gap-3 cursor-pointer transition-transform duration-200 hover:bg-slate-50 dark:hover:bg-slate-800/30 px-1 py-2.5"
             data-tx-toggle="${tx.id}">
          <div class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-[10px] text-xl"
               style="background-color: ${catBg};">
            ${tx.category.icon}
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-medium text-sm truncate text-slate-900 dark:text-slate-100">${tx.category.name}</div>
            <div class="text-xs truncate text-slate-400">${tx.note || ""}</div>
          </div>
          <div class="flex-shrink-0 text-right">
            <div class="text-sm font-semibold ${colorClass}">
              ${sign}${formatMoney(tx.amount, tx.account?.currency)}
            </div>
            ${userName ? `<div class="truncate text-[9px] leading-tight text-slate-400 max-w-[80px] opacity-70">${userName}</div>` : ""}
          </div>
        </div>
        ${isExpanded && isDeletable && this.editingTxId !== tx.id ? `
          <div class="animate-fade-in flex gap-2 px-1 pb-2.5 justify-end">
            <button data-tx-edit="${tx.id}"
                    class="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold cursor-pointer hover:opacity-80 transition-opacity"
                    style="background-color: rgba(99, 102, 241, 0.1); color: rgb(99, 102, 241);">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              ${t("dashboard.edit") || "Edit"}
            </button>
            <button data-tx-delete="${tx.id}"
                    class="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold cursor-pointer hover:opacity-80 transition-opacity"
                    style="background-color: rgba(239, 68, 68, 0.1); color: rgb(239, 68, 68);">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
              </svg>
              ${t("dashboard.delete") || "Delete"}
            </button>
          </div>` : ""}
        ${this.editingTxId === tx.id ? this.editForm(tx) : ""}
      </div>`
  }

  editForm(tx) {
    const cats = this.categoriesCache || []
    const filteredCats = cats.filter(c => c.kind === tx.kind)
    const catOptions = filteredCats.map(c =>
      `<option value="${c.id}" ${c.id === tx.category.id ? "selected" : ""}>${c.icon} ${c.name}</option>`
    ).join("")

    return `
      <div class="animate-fade-in px-1 pb-2.5">
        <div class="flex flex-col gap-2">
          <div>
            <select data-edit-category="${tx.id}"
                    class="w-full px-3 py-2.5 rounded-xl text-sm bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 appearance-none">
              ${catOptions}
            </select>
          </div>
          <div class="flex gap-2">
            <input type="number" data-edit-amount="${tx.id}" value="${tx.amount}" step="0.01" min="0.01"
                   placeholder="${t("dashboard.edit_amount") || "Amount"}"
                   class="flex-1 px-3 py-2.5 rounded-xl text-sm bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700" />
            <input type="text" data-edit-note="${tx.id}" value="${(tx.note || "").replace(/"/g, "&quot;")}"
                   placeholder="${t("dashboard.edit_note") || "Note"}"
                   class="flex-[2] px-3 py-2.5 rounded-xl text-sm bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700" />
          </div>
          <div class="flex gap-2 justify-end">
            <button data-tx-cancel="${tx.id}"
                    class="px-4 py-2 rounded-xl text-[13px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-pointer hover:opacity-80 transition-opacity">
              ${t("dashboard.cancel") || "Cancel"}
            </button>
            <button data-tx-save="${tx.id}"
                    class="px-4 py-2 rounded-xl text-[13px] font-semibold bg-indigo-500 text-white cursor-pointer hover:bg-indigo-600 transition-colors">
              ${t("dashboard.save") || "Save"}
            </button>
          </div>
        </div>
      </div>`
  }

  async startEdit(txId) {
    if (!networkStatus.online) { alertOffline(); return }
    if (!this.categoriesCache) {
      try {
        const data = await apiGet("/api/categories")
        this.categoriesCache = Array.isArray(data) ? data : (data.categories || [])
      } catch (e) {
        if (isOfflineError(e)) { alertOffline(); return }
        console.error("Failed to load categories:", e)
        return
      }
    }
    this.editingTxId = txId
    this.expandedTxId = txId
    this.renderTransactions()
  }

  async saveEdit(txId) {
    if (!networkStatus.online) { alertOffline(); return }
    const amount = parseFloat(this.transactionsTarget.querySelector(`[data-edit-amount="${txId}"]`)?.value)
    const note = this.transactionsTarget.querySelector(`[data-edit-note="${txId}"]`)?.value
    const categoryId = parseInt(this.transactionsTarget.querySelector(`[data-edit-category="${txId}"]`)?.value)

    if (!amount || amount <= 0) return

    try {
      const updated = await apiPut(`/api/transactions/${txId}`, {
        transaction: { amount, note, category_id: categoryId }
      })

      const idx = this.allTransactions.findIndex(tx => tx.id === txId)
      if (idx !== -1) this.allTransactions[idx] = updated

      this.editingTxId = null
      this.renderTransactions()

      const data = await apiGet(this.buildApiUrl(1))
      const currency = data.currency || "RUB"
      if (this.hasExpensesTarget) this.expensesTarget.textContent = formatMoney(data.total_expenses, currency)
      if (this.hasIncomeTarget) this.incomeTarget.textContent = formatMoney(data.total_income, currency)
      if (this.hasBalanceTarget) this.balanceTarget.textContent = formatMoney(data.total_balance, currency)
    } catch (e) {
      console.error("Save error:", e)
    }
  }

  cancelEdit() {
    this.editingTxId = null
    this.renderTransactions()
  }

  async deleteTransaction(txId) {
    if (!networkStatus.online) { alertOffline(); return }
    const confirmed = confirm(t("dashboard.delete_confirm") || "Delete this transaction?")
    if (!confirmed) return

    try {
      await apiDelete(`/api/transactions/${txId}`)
      this.allTransactions = this.allTransactions.filter(tx => tx.id !== parseInt(txId))
      this.renderTransactions()

      const data = await apiGet(this.buildApiUrl(1))
      const currency = data.currency || "RUB"
      if (this.hasExpensesTarget) this.expensesTarget.textContent = formatMoney(data.total_expenses, currency)
      if (this.hasIncomeTarget) this.incomeTarget.textContent = formatMoney(data.total_income, currency)
      if (this.hasBalanceTarget) this.balanceTarget.textContent = formatMoney(data.total_balance, currency)
    } catch (e) {
      console.error("Delete error:", e)
    }
  }

  switchPeriod(event) {
    const btn = event.currentTarget
    this.currentPeriod = btn.dataset.period

    this.element.querySelectorAll("[data-period]").forEach(el => {
      el.classList.remove("bg-indigo-500", "text-white")
      el.classList.add("bg-slate-100", "dark:bg-slate-800", "text-slate-500", "dark:text-slate-400")
    })
    btn.classList.remove("bg-slate-100", "dark:bg-slate-800", "text-slate-500", "dark:text-slate-400")
    btn.classList.add("bg-indigo-500", "text-white")

    if (this.currentPeriod === "custom") {
      if (this.hasCustomRangeTarget) {
        this.customRangeTarget.classList.remove("hidden")
        const today = new Date().toISOString().slice(0, 10)
        const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)
        if (this.hasStartDateTarget && !this.startDateTarget.value) this.startDateTarget.value = monthAgo
        if (this.hasEndDateTarget && !this.endDateTarget.value) this.endDateTarget.value = today
        this.customStartDate = this.startDateTarget.value
        this.customEndDate = this.endDateTarget.value
      }
    } else {
      if (this.hasCustomRangeTarget) this.customRangeTarget.classList.add("hidden")
    }

    this.load()
  }

  applyCustomRange() {
    if (!this.hasStartDateTarget || !this.hasEndDateTarget) return
    this.customStartDate = this.startDateTarget.value
    this.customEndDate = this.endDateTarget.value
    if (this.customStartDate && this.customEndDate) {
      this.load()
    }
  }

  // ── Voice Recording ──

  async startVoice() {
    if (!networkStatus.online) { alertOffline(); return }
    if (!this.isPremium) {
      window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "voice" } }))
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.mediaRecorder = new MediaRecorder(stream, { mimeType: this.getSupportedMimeType() })
      this.audioChunks = []

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this.audioChunks.push(e.data)
      }

      this.mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop())
        this.processVoice()
      }

      this.mediaRecorder.start()
      this.voiceSeconds = 0
      this.showVoiceOverlay()
      this.voiceInterval = setInterval(() => {
        this.voiceSeconds++
        const m = Math.floor(this.voiceSeconds / 60)
        const s = String(this.voiceSeconds % 60).padStart(2, "0")
        if (this.hasVoiceTimerTarget) this.voiceTimerTarget.textContent = `${m}:${s}`
      }, 1000)
    } catch (e) {
      console.error("Microphone access error:", e)
      alert(t("dashboard.voice_error") || "Could not access microphone")
    }
  }

  getSupportedMimeType() {
    const types = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"]
    return types.find(t => MediaRecorder.isTypeSupported(t)) || ""
  }

  showVoiceOverlay() {
    if (this.hasVoiceOverlayTarget) {
      this.voiceOverlayTarget.classList.remove("hidden")
      if (this.hasVoiceStatusTarget) this.voiceStatusTarget.textContent = t("dashboard.voice_recording") || "Recording..."
      if (this.hasVoiceTimerTarget) this.voiceTimerTarget.textContent = "0:00"
    }
  }

  stopVoice() {
    if (this.voiceInterval) {
      clearInterval(this.voiceInterval)
      this.voiceInterval = null
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop()
    }
  }

  async processVoice() {
    if (this.hasVoiceOverlayTarget) this.voiceOverlayTarget.classList.add("hidden")

    if (!this.audioChunks.length) return

    this.showProcessing(t("dashboard.voice_recognizing") || "Recognizing...")

    const mimeType = this.mediaRecorder.mimeType || "audio/webm"
    const ext = mimeType.includes("ogg") ? "ogg" : mimeType.includes("mp4") ? "m4a" : "webm"
    const blob = new Blob(this.audioChunks, { type: mimeType })
    const formData = new FormData()
    formData.append("audio", blob, `voice.${ext}`)

    try {
      const result = await apiUpload("/api/transactions/voice", formData)
      this.hideProcessing()
      this.showResults(result.transactions, result.transcription)
    } catch (e) {
      this.hideProcessing()
      if (e.status === 403 && e.message === "ai_consent_required") {
        const accepted = await showAiConsentModal()
        if (accepted) this.stopRecording()
        return
      }
      if (e.status === 403) {
        alert(t("dashboard.premium_required_voice") || "Voice input is a Premium feature")
      } else {
        alert(e.message || t("dashboard.voice_error"))
      }
    }
  }

  // ── Photo Receipt ──

  startPhoto() {
    if (!networkStatus.online) { alertOffline(); return }
    if (!this.isPremium) {
      window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "photo" } }))
      return
    }
    if (this.hasPhotoInputTarget) {
      this.photoInputTarget.click()
    }
  }

  async onPhotoSelected() {
    const file = this.photoInputTarget.files[0]
    if (!file) return

    this.showProcessing(t("dashboard.photo_analyzing") || "Analyzing receipt...")

    const formData = new FormData()
    formData.append("photo", file)

    try {
      const result = await apiUpload("/api/transactions/photo", formData)
      this.hideProcessing()
      this.showResults(result.transactions)
    } catch (e) {
      this.hideProcessing()
      if (e.status === 403 && e.message === "ai_consent_required") {
        const accepted = await showAiConsentModal()
        if (accepted) this.onPhotoSelected()
        return
      }
      if (e.status === 403) {
        alert(t("dashboard.premium_required_photo") || "Receipt scanning is a Premium feature")
      } else {
        alert(e.message || t("dashboard.photo_error"))
      }
    } finally {
      this.photoInputTarget.value = ""
    }
  }

  // ── Processing & Results Overlays ──

  showProcessing(text) {
    if (this.hasProcessingOverlayTarget) {
      this.processingOverlayTarget.classList.remove("hidden")
      if (this.hasProcessingTextTarget) this.processingTextTarget.textContent = text
    }
  }

  hideProcessing() {
    if (this.hasProcessingOverlayTarget) this.processingOverlayTarget.classList.add("hidden")
  }

  showResults(transactions, transcription) {
    if (!transactions || !transactions.length) {
      this.load()
      return
    }

    // Single transaction — just reload dashboard
    if (transactions.length === 1 && !transactions[0].transfer) {
      this.load()
      return
    }

    // Multiple transactions — show results overlay
    if (this.hasResultsOverlayTarget && this.hasResultsContentTarget) {
      const count = transactions.length
      let html = `
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">
            ${count} ${t("dashboard.items_found") || "items found"}
          </h3>
          <button data-action="click->pwa-dashboard#closeResults"
                  class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 hover:bg-slate-200">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>`

      if (transcription) {
        html += `<p class="text-sm text-slate-400 mb-3 italic">"${transcription}"</p>`
      }

      html += `<div class="space-y-2">`
      transactions.forEach(tx => {
        if (tx.transfer) {
          html += `
            <div class="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <div class="w-10 h-10 rounded-[10px] bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center text-xl">↔</div>
              <div class="flex-1">
                <div class="text-sm font-medium text-slate-900 dark:text-slate-100">${tx.from} → ${tx.to}</div>
              </div>
              <div class="text-sm font-semibold text-indigo-500">${formatMoney(tx.amount)}</div>
            </div>`
        } else {
          const sign = tx.kind === "expense" ? "-" : "+"
          const colorClass = tx.kind === "expense" ? "text-red-500" : "text-emerald-500"
          const catBg = tx.category?.color ? `color-mix(in srgb, ${tx.category.color} 10%, transparent)` : "rgb(248, 250, 252)"
          html += `
            <div class="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <div class="w-10 h-10 rounded-[10px] flex items-center justify-center text-xl" style="background-color: ${catBg};">${tx.category?.icon || ""}</div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">${tx.category?.name || ""}</div>
                <div class="text-xs text-slate-400 truncate">${tx.note || ""}</div>
              </div>
              <div class="text-sm font-semibold ${colorClass}">${sign}${formatMoney(tx.amount, tx.account?.currency)}</div>
            </div>`
        }
      })
      html += `</div>
        <button data-action="click->pwa-dashboard#closeResults"
                class="w-full mt-4 py-3 rounded-2xl font-semibold text-center bg-indigo-500 text-white hover:bg-indigo-600 active:scale-[0.98] transition-all">
          OK
        </button>`

      this.resultsContentTarget.innerHTML = html
      this.resultsOverlayTarget.classList.remove("hidden")
    } else {
      this.load()
    }
  }

  closeResults() {
    if (this.hasResultsOverlayTarget) this.resultsOverlayTarget.classList.add("hidden")
    this.load()
  }
}
